import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { apiService } from '../services/api';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  padding: 2rem;
  text-align: center;
`;

const Title = styled.h1`
  margin: 0 0 1rem 0;
  font-size: 1.6rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Msg = styled.div<{ type: 'error'|'success'|'info' }>`
  margin-bottom: 1.5rem;
  padding: 1rem;
  border-radius: 10px;
  color: #fff;
  background: ${p => {
    if (p.type === 'success') return 'linear-gradient(135deg,#11998e,#38ef7d)';
    if (p.type === 'error') return 'linear-gradient(135deg,#fc4a1a,#f7b733)';
    return 'linear-gradient(135deg,#667eea,#764ba2)';
  }};
  font-weight: 600;
`;

const StyledLink = styled(Link)`
  display: inline-block;
  margin-top: 1rem;
  color: #667eea;
  text-decoration: none;
  font-weight: 700;
  &:hover {
    text-decoration: underline;
  }
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #667eea;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const VerifyEmail: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState<string>('Verifying your email...');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid or missing verification token.');
        return;
      }

      try {
        const resp = await apiService.get<any>(`/api/auth/verify-email?token=${token}`);
        setStatus('success');
        setMessage(resp?.message || 'Email verified successfully!');
        setTimeout(() => navigate('/portal/login'), 3000);
      } catch (e: any) {
        setStatus('error');
        setMessage(e?.message || 'Email verification failed. The link may be expired or invalid.');
      }
    };

    verify();
  }, [token, navigate]);

  return (
    <Wrapper>
      <Card>
        <Title>Email Verification</Title>
        {status === 'verifying' && (
          <>
            <Spinner />
            <Msg type="info">{message}</Msg>
          </>
        )}
        {status === 'success' && (
          <>
            <Msg type="success">✅ {message}</Msg>
            <p>Redirecting to login...</p>
            <StyledLink to="/portal/login">Click here if not redirected</StyledLink>
          </>
        )}
        {status === 'error' && (
          <>
            <Msg type="error">⚠️ {message}</Msg>
            <StyledLink to="/portal/register">Back to Registration</StyledLink>
            <br />
            <StyledLink to="/">Back to Home</StyledLink>
          </>
        )}
      </Card>
    </Wrapper>
  );
};

export default VerifyEmail;
